import { useHandler } from "@redotech/react-util/hook";
import { CheckoutProbabilitySegment } from "@redotech/redo-conversion-core-common/checkouts/checkout-tree";
import { checkoutProbabilityExpressionEqual } from "@redotech/redo-conversion-core-common/checkouts/checkout-tree-equal";
import {
  DefaultCheckoutProbabilityBucket,
  defaultCheckoutProbabilityExpressions,
} from "@redotech/redo-conversion-core-common/checkouts/segments";
import {
  rcoCheckoutProbabilitySegmentSchema,
  RcoSegmentStep,
} from "@redotech/redo-flows-common/advanced-flow-db-parser";
import { useTranslation } from "@redotech/redo-i18n";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo } from "react";
import { PartialWithRequiredType } from "../..";

function getCurrentBucketFromExpressions(
  expressions: CheckoutProbabilitySegment["expressions"],
): DefaultCheckoutProbabilityBucket {
  for (const bucket of Object.values(DefaultCheckoutProbabilityBucket)) {
    if (typeof bucket !== "number") {
      continue;
    }

    const bucketExpressions = defaultCheckoutProbabilityExpressions[bucket];
    if (checkoutProbabilityExpressionEqual(expressions, bucketExpressions)) {
      return bucket;
    }
  }

  return DefaultCheckoutProbabilityBucket.WARM;
}

export const CheckoutProbabilityInspectorContent = memo(
  function CheckoutProbabilityInspectorContent({
    step,
  }: {
    step: RcoSegmentStep;
  }) {
    const { t } = useTranslation();
    const segment = useMemo(
      () => rcoCheckoutProbabilitySegmentSchema.parse(step.segment),
      [step],
    );

    const currentBucket = useMemo(
      () => getCurrentBucketFromExpressions(segment.expressions),
      [segment.expressions],
    );

    return (
      <Flex align="flex-start" dir="column" gap="xs">
        <Text fontSize="xs" fontWeight="medium" textColor="secondary">
          {t("Condition")}
        </Text>
        <Text fontSize="xs" fontWeight="medium" textColor="primary">
          {currentBucket === DefaultCheckoutProbabilityBucket.COLD
            ? t("Checkout probability score is cold")
            : currentBucket === DefaultCheckoutProbabilityBucket.COOL
              ? t("Checkout probability score is cool")
              : currentBucket === DefaultCheckoutProbabilityBucket.HOT
                ? t("Checkout probability score is hot")
                : t("Checkout probability score is warm")}
        </Text>
      </Flex>
    );
  },
);

export const EditableCheckoutProbabilityInspectorContent = memo(
  function EditableCheckoutProbabilityInspectorContent({
    step,
    setStep,
  }: {
    step: Omit<RcoSegmentStep, "matchingNextId" | "nonMatchingNextId"> & {
      matchingNextId?: string | undefined;
      nonMatchingNextId?: string | undefined;
    };
    setStep: (step: PartialWithRequiredType<RcoSegmentStep>) => void;
  }) {
    const { t } = useTranslation();
    const segment = useMemo(
      () => rcoCheckoutProbabilitySegmentSchema.parse(step.segment),
      [step],
    );

    const currentBucket = useMemo(
      () => getCurrentBucketFromExpressions(segment.expressions),
      [segment.expressions],
    );

    const bucketOptions: RedoListItem<DefaultCheckoutProbabilityBucket>[] =
      useMemo(
        () => [
          {
            type: "text",
            id: DefaultCheckoutProbabilityBucket.COLD,
            text: t("cold"),
            value: DefaultCheckoutProbabilityBucket.COLD,
          },
          {
            type: "text",
            id: DefaultCheckoutProbabilityBucket.COOL,
            text: t("cool"),
            value: DefaultCheckoutProbabilityBucket.COOL,
          },
          {
            type: "text",
            id: DefaultCheckoutProbabilityBucket.WARM,
            text: t("warm"),
            value: DefaultCheckoutProbabilityBucket.WARM,
          },
          {
            type: "text",
            id: DefaultCheckoutProbabilityBucket.HOT,
            text: t("hot"),
            value: DefaultCheckoutProbabilityBucket.HOT,
          },
        ],
        [t],
      );

    const selectedBucketOption = useMemo(
      () => bucketOptions.find((option) => option.value === currentBucket),
      [bucketOptions, currentBucket],
    );

    const handleBucketChange = useHandler(
      (args: { item: RedoListItem<DefaultCheckoutProbabilityBucket> }) => {
        const newBucket = args.item.value;
        const newExpressions = defaultCheckoutProbabilityExpressions[newBucket];

        setStep({
          ...step,
          segment: { ...segment, expressions: newExpressions },
        });
      },
    );

    return (
      <Flex align="flex-start" dir="column" gap="xs">
        <RedoSingleSelectDropdownInput<DefaultCheckoutProbabilityBucket>
          label={t("Checkout probability score")}
          options={bucketOptions}
          optionSelected={handleBucketChange}
          selectedItem={selectedBucketOption}
          size="xs"
        />
      </Flex>
    );
  },
);
